import { useEffect, useState } from "react";

interface PreloaderOptions {
  minDisplayTime?: number;
  imageSrcs?: string[];
  waitForFonts?: boolean;
}

/**
 * Hook that manages splash screen preloading state with configurable timing
 * and font loading. Ensures splash screen displays for a minimum time while
 * resources load.
 */
let globalAssetsLoaded = false;
let globalLoadProgress = 0;

export function useSplashPreloader({
  minDisplayTime = 2000,
  imageSrcs = [],
  waitForFonts = true,
}: PreloaderOptions = {}) {
  const [isReady, setIsReady] = useState(globalAssetsLoaded);
  const [progress, setProgress] = useState(globalLoadProgress);

  useEffect(() => {
    if (globalAssetsLoaded) {
      setIsReady(true);
      setProgress(100);
      return;
    }

    let canceled = false;

    const updateProgress = (value: number) => {
      if (!canceled) {
        setProgress(value);
        globalLoadProgress = value;
      }
    };

    const promises: Promise<void>[] = [];
    let loaded = 0;
    const totalItems = imageSrcs.length + (waitForFonts ? 1 : 0) + 1; // +1 for min time

    const minTimePromise = new Promise<void>((resolve) => {
      setTimeout(() => {
        loaded++;
        updateProgress(Math.floor((loaded / totalItems) * 100));
        resolve();
      }, minDisplayTime);
    });
    promises.push(minTimePromise);

    if (waitForFonts && document?.fonts?.ready) {
      const fontPromise = document.fonts.ready.then(() => {
        loaded++;
        updateProgress(Math.floor((loaded / totalItems) * 100));
      });
      promises.push(fontPromise);
    }

    const uniqueImages = [...new Set(imageSrcs.filter(Boolean))];

    uniqueImages.forEach((src) => {
      const imgPromise = new Promise<void>((resolve) => {
        try {
          const img = new Image();

          const timeout = setTimeout(() => {
            console.warn(`Timed out loading image: ${src}`);
            loaded++;
            updateProgress(Math.floor((loaded / totalItems) * 100));
            resolve();
          }, 3000);

          img.onload = () => {
            clearTimeout(timeout);
            loaded++;
            updateProgress(Math.floor((loaded / totalItems) * 100));
            resolve();
          };

          img.onerror = () => {
            clearTimeout(timeout);
            console.warn(`Failed to load image: ${src}`);
            loaded++;
            updateProgress(Math.floor((loaded / totalItems) * 100));
            resolve();
          };

          img.src = src;
        } catch (e) {
          console.error(`Error setting up image load for ${src}`, e);
          loaded++;
          updateProgress(Math.floor((loaded / totalItems) * 100));
          resolve();
        }
      });

      promises.push(imgPromise);
    });

    const failsafe = setTimeout(() => {
      if (!canceled && !globalAssetsLoaded) {
        globalAssetsLoaded = true;
        updateProgress(100);
        setIsReady(true);
      }
    }, 5000);

    Promise.all(promises)
      .then(() => {
        if (!canceled) {
          globalAssetsLoaded = true;
          updateProgress(100);
          setIsReady(true);
        }
      })
      .catch((err) => {
        console.error("Error in preloading:", err);
        if (!canceled) {
          globalAssetsLoaded = true;
          updateProgress(100);
          setIsReady(true);
        }
      });

    return () => {
      canceled = true;
      clearTimeout(failsafe);
    };
  }, []);

  return {
    isReady,
    progress,
    loadingState: isReady ? "ready" : ("loading" as const),
  };
}
